import React from "react"
import * as Tabs from "@radix-ui/react-tabs"
import type { MembershipPricingOption, Price, PricingOption } from "types"

import { Accordion } from "packages/ui-shared/components/Accordion/Accordion"

import {
  membershipContentContainer,
  membershipContentFooter,
  membershipContentHeader,
  perksStyling,
  priceExplainerStyles,
} from "./Pricing.css"
import { TabsContent, TabsList, TabsTrigger } from "./PillTabs.css"
import { SeeMore } from "packages/ui-shared/components/SeeMore/SeeMore"
import { useSegment } from "tracking"

const PRICING_COMPONENT_RADIO_NAME = "pricing-toggle"

type AccordionWithMultipleMembershipOptionsProps = {
  selectedValue?: string
  onAccordionChange: (id: Price["id"]) => void
  prices: Price[]
  setSelectedMembershipSKU: (value: string) => void
}

export const AccordionWithMultipleMembershipOptions: React.FC<
  AccordionWithMultipleMembershipOptionsProps
> = ({
  selectedValue,
  onAccordionChange,
  prices,
  setSelectedMembershipSKU,
}) => {
  const { track } = useSegment()

  return (
    <Accordion
      selectedValue={selectedValue}
      onChange={onAccordionChange}
      radioName={PRICING_COMPONENT_RADIO_NAME}
    >
      {prices.map(
        ({
          price,
          id,
        }: {
          price: MembershipPricingOption | PricingOption
          id: Price["id"]
        }) => {
          const priceMemberships =
            "memberships" in price ? price.memberships : null

          const defaultMembership = priceMemberships?.find(
            (memb) => memb.isDefault
          )

          return (
            <Accordion.Item
              value={id}
              key={id}
              radioName={PRICING_COMPONENT_RADIO_NAME}
            >
              <Accordion.Item.Label
                value={id}
                header={price.title}
                description={price.subtitle ?? ""}
                rightContent={price.priceText}
                radioName={PRICING_COMPONENT_RADIO_NAME}
              />
              {priceMemberships && (
                <Accordion.Item.Content>
                  <Tabs.Root
                    className="TabsRoot"
                    defaultValue={defaultMembership?.sku}
                    onValueChange={(value) => {
                      setSelectedMembershipSKU(value)

                      const tabMembership = priceMemberships.find(
                        ({ sku }) => sku === value
                      )
                      track({
                        event: "Page Element Clicked",
                        properties: {
                          module_content: "og_pdp_panel",
                          module_content2: defaultMembership?.sku,
                          location_type: "og_pdp_panel",
                          location_type_details: defaultMembership?.name,
                          module_content_details: "og_membership_tab",
                          module_content_details2: tabMembership?.sku,
                          module_content_details3: tabMembership?.name,
                        },
                      })
                    }}
                  >
                    <Tabs.List
                      className={TabsList}
                      aria-label="Select a membership"
                    >
                      {priceMemberships.map(({ sku, name }) => {
                        return (
                          <Tabs.Trigger
                            className={TabsTrigger}
                            value={sku}
                            key={sku}
                          >
                            {name}
                          </Tabs.Trigger>
                        )
                      })}
                    </Tabs.List>
                    {priceMemberships.map(
                      ({ sku, name, priceExplainer, perks, terms }) => {
                        return (
                          <Tabs.Content
                            className={TabsContent}
                            value={sku}
                            key={sku}
                          >
                            <div className={membershipContentContainer}>
                              <div className={perksStyling}>
                                <header className={membershipContentHeader}>
                                  <b>{name}</b>
                                </header>
                                <div className={priceExplainerStyles}>
                                  {priceExplainer}
                                </div>
                                <SeeMore
                                  isList
                                  onToggle={(open) => {
                                    track({
                                      event: "Page Element Clicked",
                                      properties: {
                                        module_content: "og_pdp_panel",
                                        module_content2:
                                          "default membership. sku value",
                                        location_type: "og_pdp_panel",
                                        location_type_details:
                                          "default membership. sku value",
                                        module_content_details: "view_link",
                                        module_content_details2: open
                                          ? "See More"
                                          : "See Less",
                                      },
                                    })
                                  }}
                                >
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: perks,
                                    }}
                                  />
                                </SeeMore>
                              </div>
                            </div>
                            {terms?.termsContent && (
                              <footer
                                className={membershipContentFooter}
                                dangerouslySetInnerHTML={{
                                  __html: terms.termsContent,
                                }}
                              />
                            )}
                          </Tabs.Content>
                        )
                      }
                    )}
                  </Tabs.Root>
                </Accordion.Item.Content>
              )}
            </Accordion.Item>
          )
        }
      )}
    </Accordion>
  )
}
